import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

interface Props {
    value: string;         // "HH:mm" — e.g. "20:00"
    onChange: (next: string) => void;
    disabled?: boolean;
    ariaLabel?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

/**
 * Custom 24h time picker — replaces the native <input type="time"> whose
 * dropdown UI is OS-dependent and looks dated. Two side-by-side scrollable
 * columns: hours (00–23) and minutes (15-min snaps).
 *
 * UX rules:
 *   - Picking an hour keeps the popover open (user usually adjusts both).
 *   - Picking a minute closes the popover (commit + done).
 *   - On open, both columns auto-scroll to the currently selected value.
 *   - Click outside or press Escape to dismiss without changes.
 */
const TimeDropdown: React.FC<Props> = ({ value, onChange, disabled, ariaLabel }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const hourColRef = useRef<HTMLDivElement>(null);
    const minuteColRef = useRef<HTMLDivElement>(null);

    // Popover position is computed via getBoundingClientRect on open + resize/
    // scroll, then rendered via createPortal to document.body so the parent's
    // overflow:hidden can't clip it. Same approach used by the notification
    // bell dropdown for the same reason.
    const [popPos, setPopPos] = useState<{ top: number; left: number } | null>(null);

    const computePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        // 6px below the trigger, left-aligned. Clamp to viewport so we never
        // render off-screen on a narrow window.
        const popWidth = 168;
        let left = rect.left;
        const maxLeft = window.innerWidth - popWidth - 8;
        if (left > maxLeft) left = maxLeft;
        if (left < 8) left = 8;
        setPopPos({ top: rect.bottom + 6, left });
    }, []);

    // Parse "HH:mm" defensively — accept "HH:mm:ss" too in case a caller passes
    // the backend's HH:mm:ss format.
    const [hh, mm] = (value || '00:00').split(':');
    const currentHour = (hh || '00').padStart(2, '0');
    // Snap incoming minute to the nearest valid choice so we always highlight
    // SOMETHING. If a backend value is "20:23", we still display "20:23" in
    // the trigger but the dropdown highlights the closest snap point (20:15).
    const currentMinute = (mm || '00').substring(0, 2);
    const snappedMinute = MINUTES.includes(currentMinute)
        ? currentMinute
        : nearestSnap(currentMinute);

    // Close on outside click or Escape. With portal rendering we have to
    // check BOTH the trigger container AND the popover (which now lives in
    // document.body, not inside containerRef).
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            const inTrigger = containerRef.current?.contains(e.target as Node);
            const inPopover = popoverRef.current?.contains(e.target as Node);
            if (!inTrigger && !inPopover) setOpen(false);
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    // Keep popover glued to the trigger when the page resizes or scrolls.
    useEffect(() => {
        if (!open) return;
        computePosition();
        const onChange = () => computePosition();
        window.addEventListener('resize', onChange);
        window.addEventListener('scroll', onChange, true);
        return () => {
            window.removeEventListener('resize', onChange);
            window.removeEventListener('scroll', onChange, true);
        };
    }, [open, computePosition]);

    // On open, scroll each column so the currently selected cell is visible.
    useEffect(() => {
        if (!open) return;
        // Defer to next tick so the popover is mounted.
        const id = window.requestAnimationFrame(() => {
            scrollToSelected(hourColRef.current);
            scrollToSelected(minuteColRef.current);
        });
        return () => window.cancelAnimationFrame(id);
    }, [open]);

    const handleHourClick = useCallback((h: string) => {
        onChange(`${h}:${snappedMinute}`);
        // Keep the popover open so the user can adjust minute next.
    }, [snappedMinute, onChange]);

    const handleMinuteClick = useCallback((m: string) => {
        onChange(`${currentHour}:${m}`);
        setOpen(false);
    }, [currentHour, onChange]);

    const handleToggleOpen = () => {
        if (!open) computePosition();
        setOpen((v) => !v);
    };

    const popoverJsx = open && popPos && (
        <div
            className="time-dd__popover"
            role="dialog"
            ref={popoverRef}
            style={{ position: 'fixed', top: popPos.top, left: popPos.left }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div className="time-dd__headers">
                <span className="time-dd__header">
                    <FormattedMessage id="notifications.settings.time.hourHeader" defaultMessage="Hour" />
                </span>
                <span className="time-dd__header-gap" aria-hidden="true" />
                <span className="time-dd__header">
                    <FormattedMessage id="notifications.settings.time.minuteHeader" defaultMessage="Minute" />
                </span>
            </div>
            <div className="time-dd__columns">
                <div className="time-dd__column" ref={hourColRef} role="listbox" aria-label="Hour">
                    {HOURS.map((h) => (
                        <button
                            key={h}
                            type="button"
                            role="option"
                            aria-selected={h === currentHour}
                            data-selected={h === currentHour ? 'true' : undefined}
                            className={`time-dd__cell ${h === currentHour ? 'is-active' : ''}`}
                            onClick={() => handleHourClick(h)}
                        >
                            {h}
                        </button>
                    ))}
                </div>
                <div className="time-dd__divider" aria-hidden="true" />
                <div className="time-dd__column" ref={minuteColRef} role="listbox" aria-label="Minute">
                    {MINUTES.map((m) => (
                        <button
                            key={m}
                            type="button"
                            role="option"
                            aria-selected={m === snappedMinute}
                            data-selected={m === snappedMinute ? 'true' : undefined}
                            className={`time-dd__cell ${m === snappedMinute ? 'is-active' : ''}`}
                            onClick={() => handleMinuteClick(m)}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="time-dd" ref={containerRef}>
            <button
                ref={triggerRef}
                type="button"
                className={`time-dd__trigger ${open ? 'is-open' : ''}`}
                onClick={handleToggleOpen}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={ariaLabel}
            >
                <span className="time-dd__value">{currentHour}:{currentMinute}</span>
                <svg className="time-dd__caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {popoverJsx && createPortal(popoverJsx, document.body)}
        </div>
    );
};

function nearestSnap(minute: string): string {
    const n = parseInt(minute, 10);
    if (Number.isNaN(n)) return '00';
    const snaps = [0, 15, 30, 45];
    let best = snaps[0];
    let bestDiff = Infinity;
    for (const s of snaps) {
        const d = Math.abs(s - n);
        if (d < bestDiff) {
            bestDiff = d;
            best = s;
        }
    }
    return best.toString().padStart(2, '0');
}

function scrollToSelected(column: HTMLDivElement | null) {
    if (!column) return;
    const selected = column.querySelector<HTMLElement>('[data-selected="true"]');
    if (!selected) return;
    // Center the selected cell vertically in the scroll container.
    const containerHeight = column.clientHeight;
    const cellTop = selected.offsetTop;
    const cellHeight = selected.offsetHeight;
    column.scrollTop = cellTop - (containerHeight / 2) + (cellHeight / 2);
}

export default TimeDropdown;
